import type {Repository} from '@github-ui/current-repository'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {commitsByAuthor, userHovercardPath} from '@github-ui/paths'
import {Box, type BoxProps, Link, Text} from '@primer/react'
import type {Author} from '../commit-attribution-types'
import {isBotOrApp} from '../utils'
import type {CommitAttributionProps} from '../CommitAttribution'
import {AuthorTooltip} from './AuthorTooltip'

type AuthorAvatarProps = {
  author: Author | undefined
  repo: Repository
  sx?: BoxProps['sx']
  includeTooltip?: boolean
} & Pick<CommitAttributionProps, 'avatarSize'>

/**
 * Renders the author of a commit.
 */
export function AuthorAvatar({author, repo, avatarSize, sx = {}, includeTooltip = true}: AuthorAvatarProps) {
  if (!author) return null

  const avatar = (
    <GitHubAvatar
      aria-label={`${author.login || 'author'}`}
      src={author.avatarUrl}
      alt={`${author.login || 'author'}`}
      sx={{mr: 2, mt: '-1px'}}
      size={avatarSize}
      square={isBotOrApp(author)}
    />
  )

  return (
    <Box sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', ...sx}} data-testid="author-avatar">
      {author.path ? (
        <Link
          href={author.path}
          data-testid="avatar-icon-link"
          data-hovercard-url={author.login ? userHovercardPath({owner: author.login}) : undefined}
        >
          {avatar}
        </Link>
      ) : (
        avatar
      )}
      {author.login ? (
        <AuthorTooltip author={author} renderTooltip={includeTooltip}>
          <Link
            muted
            href={commitsByAuthor({repo, author: author.login})}
            aria-label={`commits by ${author.login}`}
            sx={{
              fontWeight: 600,
              whiteSpace: 'nowrap',
              color: 'fg.default',
              '&:hover': {color: 'fg.default', textDecoration: 'underline'},
            }}
          >
            {author.login}
          </Link>
        </AuthorTooltip>
      ) : (
        <Text sx={{fontWeight: 600, whiteSpace: 'nowrap', color: 'fg.default'}}>{author.displayName}</Text>
      )}
    </Box>
  )
}

try{ AuthorAvatar.displayName ||= 'AuthorAvatar' } catch {}